import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import OnboardingLayout from '../../components/onboarding/OnboardingLayout';
import apiClient from '../../services/api/apiClient';

function Step3ContentFormat() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editAccountId = searchParams.get('edit');
  const isEditMode = !!editAccountId;

  const [selectedFormats, setSelectedFormats] = useState([]);
  const [loading, setLoading] = useState(isEditMode);

  useEffect(() => {
    if (isEditMode && editAccountId) {
      loadAccountSettings(editAccountId);
    }
  }, [isEditMode, editAccountId]);

  const loadAccountSettings = async (accountId) => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/user/social-accounts/account-settings/id/${accountId}`);
      if (response.data.success && response.data.data.settings?.tone_styles) {
        setSelectedFormats(response.data.data.settings.tone_styles);
      }
    } catch (error) {
      console.error('載入設定失敗:', error);
    } finally {
      setLoading(false);
    }
  };

  const formats = [
    {
      id: 'professional',
      title: '專業嚴謹',
      description: '正式、有深度、專業權威感',
      icon: 'mdi:briefcase-outline'
    },
    {
      id: 'casual',
      title: '輕鬆幽默',
      description: '親切自然、詼諧有趣、容易親近',
      icon: 'mdi:emoticon-happy-outline'
    },
    {
      id: 'friendly',
      title: '溫暖友善',
      description: '溫和體貼、富同理心、如朋友般',
      icon: 'mdi:heart-outline'
    },
    {
      id: 'motivational',
      title: '激勵鼓舞',
      description: '充滿能量、積極正面、激發行動',
      icon: 'mdi:fire'
    },
    {
      id: 'concise',
      title: '簡潔直白',
      description: '乾淨俐落、一針見血、直指重點',
      icon: 'mdi:flash-outline'
    },
    {
      id: 'ai_recommended',
      title: 'AI 推薦',
      description: '讓 AI 分析你的帳號，自動推薦最適合的語氣',
      icon: 'mdi:robot-outline',
      isAI: true
    }
  ];

  const handleToggleFormat = (formatId) => {
    if (selectedFormats.includes(formatId)) {
      setSelectedFormats(selectedFormats.filter(id => id !== formatId));
    } else {
      if (selectedFormats.length < 2) {
        setSelectedFormats([...selectedFormats, formatId]);
      }
    }
  };

  const handleNext = () => {
    if (selectedFormats.length > 0) {
      localStorage.setItem('onboarding_tone_styles', JSON.stringify(selectedFormats));
      if (isEditMode && editAccountId) {
        navigate(`/onboarding/step4?edit=${editAccountId}`);
      } else {
        navigate('/onboarding/step4');
      }
    }
  };

  const handleBack = () => {
    if (isEditMode && editAccountId) {
      navigate(`/onboarding/step2?edit=${editAccountId}`);
    } else {
      navigate('/onboarding/step2');
    }
  };

  const illustration = (
    <div className="w-full max-w-md mx-auto">
      <div className="relative flex items-center justify-center">
        {/* Stack of cards/documents */}
        <div className="relative">
          {/* Back card */}
          <div className="absolute top-4 left-4 w-56 h-72 rounded-3xl bg-blue-200/40 backdrop-blur-sm shadow-md rotate-6"></div>
          {/* Middle card */}
          <div className="absolute top-2 left-2 w-56 h-72 rounded-3xl bg-blue-300/50 backdrop-blur-sm shadow-lg rotate-3"></div>
          {/* Front card */}
          <div className="relative w-56 h-72 rounded-3xl bg-white/80 backdrop-blur-sm shadow-xl flex flex-col items-center justify-center p-8 gap-4">
            <div className="w-12 h-3 bg-blue-500 rounded-full"></div>
            <div className="w-full h-2 bg-blue-200 rounded-full"></div>
            <div className="w-full h-2 bg-blue-200 rounded-full"></div>
            <div className="w-3/4 h-2 bg-blue-200 rounded-full"></div>
            <div className="mt-4 w-20 h-20 rounded-2xl bg-blue-500 flex items-center justify-center">
              <Icon icon="mdi:text" className="text-4xl text-white" />
            </div>
          </div>
        </div>

        {/* Floating checkmark */}
        <div className="absolute -top-6 -right-6 w-16 h-16 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 shadow-lg flex items-center justify-center">
          <Icon icon="mdi:check-bold" className="text-3xl text-white" />
        </div>
      </div>
    </div>
  );

  return (
    <OnboardingLayout
      currentStep={3}
      totalSteps={6}
      title="你想用什麼語氣風格？"
      illustration={illustration}
      onBack={handleBack}
      progressColor="#60A5FA"
    >
      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-6">
        <div className="flex items-start gap-3">
          <Icon icon="mdi:information-outline" className="text-blue-500 text-xl flex-shrink-0 mt-0.5" />
          <div className="text-sm text-gray-700">
            <p className="font-medium mb-1">選擇適合你的語氣風格</p>
            <p className="text-gray-600">
              選擇 1-2 個語氣風格，AI 會根據這些風格生成符合你個性的內容。也可以讓 AI 幫你推薦。
            </p>
          </div>
        </div>
      </div>

      {/* Format Options */}
      <div className="mb-6">
        <p className="text-sm text-gray-500 text-center mb-6">
          最多選擇 2 個 ({selectedFormats.length}/2)
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {formats.map((format) => {
            const isSelected = selectedFormats.includes(format.id);
            const isDisabled = !isSelected && selectedFormats.length >= 2;
            const isAI = format.isAI;

            return (
              <button
                key={format.id}
                onClick={() => handleToggleFormat(format.id)}
                disabled={isDisabled}
                className={`
                  p-6 rounded-xl border-2 transition-all text-left
                  ${isSelected
                    ? isAI
                      ? 'border-purple-500 bg-purple-50 shadow-md'
                      : 'border-blue-500 bg-blue-50 shadow-md'
                    : isDisabled
                    ? 'border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed'
                    : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
                  }
                  ${isAI ? 'relative overflow-hidden' : ''}
                `}
              >
                {/* AI Badge for AI Recommended option */}
                {isAI && (
                  <div className="absolute top-3 right-3">
                    <span className="px-2 py-1 text-xs font-bold rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-md">
                      AI
                    </span>
                  </div>
                )}

                <div className="flex items-start gap-4">
                  <div className={`
                    w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0
                    ${isSelected
                      ? isAI
                        ? 'bg-gradient-to-br from-purple-500 to-pink-500'
                        : 'bg-blue-500'
                      : 'bg-gray-100'
                    }
                  `}>
                    <Icon
                      icon={format.icon}
                      className={`text-2xl ${isSelected ? 'text-white' : 'text-gray-600'}`}
                    />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      {format.title}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {format.description}
                    </p>
                  </div>
                  {isSelected && (
                    <Icon
                      icon="mdi:check-circle"
                      className={`text-2xl flex-shrink-0 ${isAI ? 'text-purple-500' : 'text-blue-500'}`}
                    />
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Next Button */}
      <div className="flex justify-end mt-8">
        <button
          onClick={handleNext}
          disabled={selectedFormats.length === 0}
          className={`
            px-8 py-3 font-medium rounded-xl transition-colors flex items-center gap-2
            ${selectedFormats.length > 0
              ? 'bg-blue-500 text-white hover:bg-blue-600'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }
          `}
        >
          Next
          <Icon icon="mdi:arrow-right" className="text-xl" />
        </button>
      </div>
    </OnboardingLayout>
  );
}

export default Step3ContentFormat;
